import Link from "next/link";
import { type Router, withRouter } from "next/router";
import { Component } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { compose } from "redux";
import { genericEventHandler } from "@/analytics/events";
import { GlobalEvents } from "@/analytics/events/constants";
import { EmailVerification } from "@/components/app/authForms/emailVerification";
import {
  StyledAuthPageContent,
  StyledAuthPageLayout,
} from "@/components/app/authForms/layout/styles";
import { SignUpFormContainer } from "@/components/app/authForms/signUpForm";
import Layout from "@/components/app/layout/index";
import StringAccessor from "@/components/cmsConfig/stringAccessor";
import { StyledHr } from "@/components/uielements/hr";
import { ROUTES } from "@/constants/routes";
import authActions from "@/redux/auth/actions";
import { type RootState } from "@/types/app";
import { deriveReferrer } from "@/utils/global";
import { safelyGetConfig } from "@/utils/stringHelpers";

interface Props extends ReduxProps {
  router: Router;
}

class SignUp extends Component<Props> {
  static async getInitialProps() {
    return {};
  }

  componentDidMount() {
    const { emailVerificationToggle, toggleEmailVerification } = this.props;

    emailVerificationToggle && toggleEmailVerification(false);
  }

  render() {
    const { router, webConfig, emailVerificationToggle, loading } = this.props;
    const loginReferrer = deriveReferrer(router);

    return (
      <Layout
        backgroundOverride={true}
        disabled={safelyGetConfig(webConfig, "accounts.disable_accounts")}
      >
        <StyledAuthPageLayout>
          <StyledAuthPageContent>
            {!loading && emailVerificationToggle ? (
              <EmailVerification referrer={loginReferrer} />
            ) : (
              <>
                <StringAccessor tag="h1" accessor="signup.header" html={true} />

                <StringAccessor
                  tag="h3"
                  accessor="signup.subheader"
                  html={true}
                />

                <StyledHr background="transparent" />

                <SignUpFormContainer />

                <StyledHr />
                <p>
                  <StringAccessor accessor="login.prompt" html={true} />
                  &nbsp;
                  <Link href={`${ROUTES.LOGIN}?referrer=${loginReferrer}`}>
                    <a
                      onClick={() =>
                        genericEventHandler(GlobalEvents.AUTH__CLICK_SIGN_IN)
                      }
                    >
                      <StringAccessor accessor="login.cta" />
                    </a>
                  </Link>
                </p>
              </>
            )}
          </StyledAuthPageContent>
        </StyledAuthPageLayout>
      </Layout>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  emailVerificationToggle: state.app.auth.emailVerificationToggle,
  loading: state.app.auth.loading,
  webConfig: state.app.cmsConfig.webConfig,
});

const mapDispatchToProps = {
  toggleEmailVerification: authActions.toggleEmailVerification,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default compose(connector, withRouter)(SignUp);
